import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, Users, Activity, Calendar, ArrowRight, Download } from "lucide-react";
import { useCanonical } from "@/lib/useCanonical";

interface CohortData {
  total_users: number;
  total_checkins: number;
  avg_energy: number;
  avg_sleep: number;
  avg_brain_fog: number;
  avg_stress: number;
  good_day_pct: number;
  crash_day_pct: number;
  by_dow: Array<{ dow: number; avg_energy: number; n: number }>;
  top_tags: Array<{ tag: string; occurrences: number; avg_energy_when_tagged: number }>;
  ms_types: Array<{ ms_type: string; n: number }>;
  refreshed_at: string;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const StateOfMsEnergy = () => {
  useCanonical({ path: "/state-of-ms-energy" });
  const [data, setData] = useState<CohortData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "State of MS Energy 2026 · Bright Side of MS";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute(
      "content",
      "The first anonymized dataset on how people living with multiple sclerosis experience energy, sleep, and fatigue day to day. Updated daily."
    );
  }, []);

  useEffect(() => {
    (async () => {
      const { data: cohortData, error } = await supabase.rpc("get_cohort_metrics");
      if (!error && cohortData) setData(cohortData as unknown as CohortData);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-16 space-y-6">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (!data || !data.total_users) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-24 text-center">
        <h1 className="font-heading text-3xl md:text-4xl font-bold mb-3">State of MS Energy</h1>
        <p className="text-muted-foreground">The dataset is still being seeded. Check back soon.</p>
      </div>
    );
  }

  const dowData = (data.by_dow || []).map((d) => ({
    day: DAY_NAMES[d.dow],
    energy: Number(d.avg_energy),
    n: d.n,
  }));
  const bestDow = dowData.length ? dowData.reduce((b, d) => (d.energy > b.energy ? d : b)) : null;
  const worstDow = dowData.length ? dowData.reduce((w, d) => (d.energy < w.energy ? d : w)) : null;

  const topBoosters = (data.top_tags || [])
    .filter((t) => t.avg_energy_when_tagged >= 3.5)
    .sort((a, b) => b.avg_energy_when_tagged - a.avg_energy_when_tagged)
    .slice(0, 5);
  const topDrainers = (data.top_tags || [])
    .filter((t) => t.avg_energy_when_tagged < 3)
    .sort((a, b) => a.avg_energy_when_tagged - b.avg_energy_when_tagged)
    .slice(0, 5);

  return (
    <main className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16">
      {/* JSON-LD for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dataset",
            name: "State of MS Energy",
            description:
              "Anonymized aggregate dataset on daily energy, sleep, brain fog, and stress reported by people living with multiple sclerosis.",
            creator: { "@type": "Organization", name: "Bright Side of MS" },
            license: "https://myelinahealth.com/privacy",
            temporalCoverage: "2025/..",
            variableMeasured: [
              "Energy level (1-5)",
              "Sleep quality (1-5)",
              "Brain fog (1-5)",
              "Stress (1-5)",
              "Day-of-week energy patterns",
              "Common energy tags",
            ],
            dateModified: data.refreshed_at,
          }),
        }}
      />

      <header className="mb-12 text-center">
        <p className="text-[11px] tracking-[0.28em] uppercase text-muted-foreground mb-4">
          The First Community-Sourced MS Energy Dataset
        </p>
        <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4 tracking-tight">
          State of MS Energy
        </h1>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Real-world data on how people living with multiple sclerosis actually experience
          energy, sleep, and fatigue, updated daily, fully anonymized, free to reference.
        </p>
        <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl h-9 gap-2"
            onClick={() => {
              const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `state-of-ms-energy-${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <Download className="h-3.5 w-3.5" /> Download aggregate dataset (JSON)
          </Button>
          <a
            href="mailto:research@myelinahealth.com?subject=Research%20partnership%20inquiry"
            className="text-[12px] text-muted-foreground hover:text-foreground underline underline-offset-4"
          >
            Research partnerships →
          </a>
        </div>
      </header>


      {/* Headline stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-12">
        {[
          { label: "People contributing", value: data.total_users.toLocaleString(), icon: Users },
          { label: "Check-ins logged", value: data.total_checkins.toLocaleString(), icon: Activity },
          { label: "Average energy", value: `${data.avg_energy}/5`, icon: TrendingUp },
          { label: "Good days", value: `${Math.round(data.good_day_pct * 100)}%`, icon: Calendar },
        ].map((s) => (
          <Card key={s.label} className="rounded-2xl border-border/60 shadow-sm">
            <CardContent className="p-5">
              <s.icon className="h-4 w-4 text-primary mb-3" />
              <div className="font-heading text-2xl md:text-3xl font-bold">{s.value}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
                {s.label}
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* Day of week */}
      <Card className="rounded-2xl border-border/60 shadow-sm mb-8">
        <CardHeader className="px-5 md:px-6 pt-5 pb-3">
          <CardTitle className="font-heading text-lg font-semibold">Energy by day of the week</CardTitle>
          <p className="text-[13px] text-muted-foreground">
            Averaged across all {data.total_users.toLocaleString()} contributors.
            {bestDow && worstDow && (
              <> Best: <strong className="text-emerald">{DAY_NAMES.map(d=>d)[dowData.indexOf(bestDow)] || bestDow.day}</strong> ({bestDow.energy}/5). Toughest: <strong className="text-destructive">{worstDow.day}</strong> ({worstDow.energy}/5).</>
            )}
          </p>
        </CardHeader>
        <CardContent className="px-5 md:px-6 pb-5">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dowData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip
                contentStyle={{
                  borderRadius: "12px",
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--card))",
                  color: "hsl(var(--foreground))",
                  fontSize: "12px",
                }}
              />
              <Bar dataKey="energy" radius={[6, 6, 0, 0]}>
                {dowData.map((d, i) => (
                  <Cell
                    key={i}
                    fill={
                      d.energy >= 3.5 ? "hsl(var(--emerald))" :
                      d.energy <= 2.5 ? "hsl(var(--destructive))" : "hsl(var(--primary))"
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Energy boosters + drainers */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader className="px-5 pt-5 pb-2">
            <CardTitle className="font-heading text-base font-semibold text-emerald">Top energy boosters</CardTitle>
            <p className="text-[12px] text-muted-foreground">Tags most linked to good energy days</p>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-2">
            {topBoosters.length ? topBoosters.map((t) => (
              <div key={t.tag} className="flex items-center justify-between border-b border-border/40 pb-2 last:border-0">
                <span className="text-sm capitalize">{t.tag}</span>
                <span className="text-xs text-muted-foreground">avg {t.avg_energy_when_tagged}/5 · {t.occurrences.toLocaleString()}×</span>
              </div>
            )) : <p className="text-sm text-muted-foreground">Not enough data yet.</p>}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader className="px-5 pt-5 pb-2">
            <CardTitle className="font-heading text-base font-semibold text-destructive">Top energy drainers</CardTitle>
            <p className="text-[12px] text-muted-foreground">Tags most linked to harder days</p>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-2">
            {topDrainers.length ? topDrainers.map((t) => (
              <div key={t.tag} className="flex items-center justify-between border-b border-border/40 pb-2 last:border-0">
                <span className="text-sm capitalize">{t.tag}</span>
                <span className="text-xs text-muted-foreground">avg {t.avg_energy_when_tagged}/5 · {t.occurrences.toLocaleString()}×</span>
              </div>
            )) : <p className="text-sm text-muted-foreground">Not enough data yet.</p>}
          </CardContent>
        </Card>
      </div>

      {/* Averages */}
      <Card className="rounded-2xl border-border/60 shadow-sm mb-12">
        <CardHeader className="px-5 md:px-6 pt-5 pb-3">
          <CardTitle className="font-heading text-lg font-semibold">Community averages</CardTitle>
        </CardHeader>
        <CardContent className="px-5 md:px-6 pb-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: "Energy", v: data.avg_energy },
            { l: "Sleep quality", v: data.avg_sleep },
            { l: "Brain fog", v: data.avg_brain_fog },
            { l: "Stress", v: data.avg_stress },
          ].map((m) => (
            <div key={m.l}>
              <div className="font-heading text-2xl font-bold">{m.v}<span className="text-sm text-muted-foreground">/5</span></div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{m.l}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Methodology + CTA */}
      <section className="rounded-2xl border border-border/60 bg-muted/30 p-6 md:p-8 mb-12">
        <h2 className="font-heading text-xl font-semibold mb-3">Methodology</h2>
        <p className="text-sm text-muted-foreground leading-relaxed mb-2">
          This dataset is refreshed daily from anonymized daily check-ins logged by consenting
          members of the Bright Side of MS community. No personal identifiers, dates, or narrative
          text is exposed. Tags appear only when logged ≥10 times across the entire cohort.
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed mb-2">
          Energy, sleep, brain fog, and stress are each self-reported on a 1–5 scale. Good days
          are defined as energy ≥ 4; harder days as energy ≤ 2.
        </p>
        <p className="text-[12px] text-muted-foreground mt-4">
          Last refreshed: {new Date(data.refreshed_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
        </p>
      </section>

      <section className="text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-3">
          Add your voice to the dataset.
        </h2>
        <p className="text-muted-foreground max-w-lg mx-auto mb-6">
          Every check-in makes the picture sharper for everyone. Access is invite only during our current cohort.
        </p>
        <Button asChild size="lg" className="rounded-xl h-12 px-6">
          <Link to="/signup">
            Request access <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </section>
    </main>
  );
};

export default StateOfMsEnergy;
